import { useEffect, useRef, useState } from "react";

import { cn } from "../../handlers/cn.utils";
import { Badge } from "../Badge/Badge";

const SearchableMultiSelectInput = ({ options, onSelect, value = [], placeholder }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	const filteredOptions = options?.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	const handleClickOutside = (event) => {
		if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
			setIsOpen(false);
		}
	};

	useEffect(() => {
		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleSelect = (selectedValue) => {
		if (value.includes(selectedValue)) {
			onSelect(value.filter((val) => val !== selectedValue));
		} else {
			onSelect([...value, selectedValue]);
		}
	};

	const handleRemoveChip = (selectedValue) => {
		onSelect(value.filter((val) => val !== selectedValue));
	};

	const isSelected = (optionValue) => value.includes(optionValue);

	return (
		<div ref={dropdownRef} className="relative w-full">
			<div
				onClick={toggleDropdown}
				className="border cursor-pointer border-gray-200 flex justify-between items-center text-sm placeholder:text-sm rounded px-2 py-0.5 placeholder:text-[#7A7A7A]"
				style={{ minHeight: "48px", gap: "8px" }}
			>
				{value.length > 0 ? (
					options
						.filter((item) => value.includes(item.value))
						.map((item) => (
							<Badge key={item.value} className="mr-1">
								<span>{item.label}</span>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveChip(item.value);
									}}
									className="text-blue-600 hover:text-blue-800 focus:outline-hidden"
								>
									&times;
								</button>
							</Badge>
						))
				) : (
					<span className="text-muted">{placeholder || "Select options"}</span>
				)}
				<i className="bi bi-chevron-down ml-auto" />
			</div>
			{isOpen && (
				<div className="absolute z-10 mt-1 rounded-md bg-white shadow-lg border border-gray-400 w-full p-2">
					<div className="text-[18px] mb-1">Select all that apply</div>
					<input
						type="text"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="w-full border border-gray-400 p-2 outline-hidden focus:ring-0 rounded mb-2"
						placeholder="Search"
					/>
					<ul className="max-h-60 overflow-y-auto">
						{filteredOptions.length > 0 ? (
							filteredOptions.map((option, index) => (
								<li
									key={index}
									className={cn("cursor-pointer p-2 text-left hover:bg-gray-200", {
										"text-gray-200": option.disabled,
										"text-gray-900": !option.disabled,
									})}
									onClick={() => handleSelect(option.value)}
								>
									<span className="d-flex items-center space-x-2">
										<input
											type="checkbox"
											id="checkbox"
											className="text-blue-600 focus:ring-blue-500"
											checked={isSelected(option.value)}
										/>
										<span>{option.label}</span>
									</span>
								</li>
							))
						) : (
							<li className="p-2 text-gray-600">No results found</li>
						)}
					</ul>
				</div>
			)}
		</div>
	);
};

export default SearchableMultiSelectInput;
